import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { contattiAzienda, numeriServizioClienti } from "../utils/data";
import Logo from "./Logo";

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-br from-[#004D4D] to-[#003333] text-white pt-20 pb-6 mt-20">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* About */}
          <div>
            <div className="mb-6">
              <Logo size="md" dark />
            </div>
            <p className="text-white/70 mb-6 text-sm leading-relaxed">
              Servizio gratuito di consulenza e comparazione offerte per luce, gas e internet.
              Nessun obbligo contrattuale: la scelta finale è sempre del cliente.
            </p>
            <div className="space-y-3">
              <div>
                <div className="flex items-center mb-2">
                  <FontAwesomeIcon icon={faPhone} className="text-secondary mr-3 text-sm" />
                  <a href={`tel:${contattiAzienda.telefonoPrincipale}`} className="text-white font-semibold text-sm hover:text-secondary transition-colors">{contattiAzienda.telefonoDisplay}</a>
                </div>
                <div className="ml-7 flex flex-wrap gap-x-4 gap-y-1">
                  {numeriServizioClienti.filter(n => !n.principale).map((n) => (
                    <a key={n.tel} href={`tel:${n.tel}`} className="text-white/50 text-xs hover:text-secondary transition-colors">{n.numero}</a>
                  ))}
                </div>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faEnvelope} className="text-secondary mr-3 text-sm" />
                <a href={`mailto:${contattiAzienda.email}`} className="text-white/70 text-sm hover:text-secondary transition-colors">{contattiAzienda.email}</a>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-secondary mr-3 text-sm" />
                <span className="text-white/70 text-sm">{contattiAzienda.indirizzo}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-base font-bold mb-6 font-display">Link Rapidi</h3>
            <ul className="space-y-3">
              {[
                { to: '/', label: 'Home' },
                { to: '/chi-siamo', label: 'Chi Siamo' },
                { to: '/offerte', label: 'Offerte' },
                { to: '/contattaci', label: 'Contattaci' },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-white/70 hover:text-secondary transition-all duration-300 inline-block hover:translate-x-1 text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-base font-bold mb-6 font-display">Servizi</h3>
            <ul className="space-y-3">
              {['Consulenza Luce', 'Consulenza Gas', 'Consulenza Internet', 'Soluzioni Business'].map((label, i) => (
                <li key={i}>
                  <Link to="/offerte" className="text-white/70 hover:text-secondary transition-all duration-300 inline-block hover:translate-x-1 text-sm">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Informazioni Legali */}
          <div>
            <h3 className="text-base font-bold mb-6 font-display">Informazioni Legali</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/privacy-policy" className="text-white/70 hover:text-secondary transition-all duration-300 inline-block hover:translate-x-1 text-sm">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/cookie-policy" className="text-white/70 hover:text-secondary transition-all duration-300 inline-block hover:translate-x-1 text-sm">
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link to="/termini-e-condizioni" className="text-white/70 hover:text-secondary transition-all duration-300 inline-block hover:translate-x-1 text-sm">
                  Termini e Condizioni
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mb-8 p-5 rounded-xl bg-white/5 border border-white/10">
          <div className="flex items-start gap-3">
            <FontAwesomeIcon icon={faInfoCircle} className="text-secondary mt-0.5 flex-shrink-0" />
            <p className="text-white/60 text-xs leading-relaxed">
              <strong className="text-white/80">Disclaimer:</strong> Rinova Group (Rinova Srl) offre un servizio gratuito 
              di consulenza e comparazione delle offerte energetiche e di connettività. Il servizio non comporta alcun costo 
              per l'utente e non implica alcun obbligo di sottoscrizione. L'eventuale stipula di un contratto con un fornitore 
              è una decisione libera e autonoma del cliente. Le informazioni e le offerte presenti su questo sito hanno 
              carattere puramente informativo e possono variare. Per i dettagli contrattuali si rimanda alla documentazione 
              ufficiale dei singoli fornitori. Rinova Srl non è un fornitore di energia, gas o servizi internet, ma un 
              intermediario indipendente che facilita il confronto tra le offerte disponibili sul mercato.
            </p>
          </div>
        </div>

        <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-white/50 text-xs text-center md:text-left">
            <p>&copy; {currentYear} Rinova Srl - P.IVA IT03994100794</p>
            <p>Sede legale: Via Fratelli Bandiera 1, 88060 Montepaone (CZ), Italia</p>
          </div>
          <div className="flex flex-wrap gap-6 justify-center">
            <Link to="/privacy-policy" className="text-white/50 hover:text-secondary text-xs transition-colors">Privacy</Link>
            <Link to="/cookie-policy" className="text-white/50 hover:text-secondary text-xs transition-colors">Cookie</Link>
            <Link to="/termini-e-condizioni" className="text-white/50 hover:text-secondary text-xs transition-colors">T&C</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
